import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import { categoriesAPI } from '../services/api';

const emptyForm = { name: '', name_ar: '', icon_url: '', gender: 'both' };

const CategoryFormPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(categoryId);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await categoriesAPI.getAll();
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        const cat = list.find((c) => String(c.id || c.uuid) === String(categoryId));
        if (!cancelled) {
          if (cat) {
            setFormData({
              name: cat.name || '',
              name_ar: cat.name_ar || '',
              icon_url: cat.icon_url || '',
              gender: cat.gender || 'both',
            });
          } else {
            toast.error('Category not found');
          }
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.message || error.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categoryId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error('Category name is required!');
      return;
    }
    try {
      if (isEdit) {
        await categoriesAPI.update(categoryId, formData);
        toast.success('Category updated successfully!');
      } else {
        await categoriesAPI.create(formData);
        toast.success('Category created successfully!');
      }
      navigate('/categories');
    } catch (error) {
      const action = isEdit ? 'updating' : 'creating';
      let message = `Error ${action} category: `;
      if (error.response?.data?.errors) {
        message += Object.values(error.response.data.errors).flat().join('\n');
      } else if (error.response?.data?.message) {
        message += error.response.data.message;
      } else {
        message += error.message || 'Unknown error';
      }
      toast.error(message);
    }
  };

  const title = isEdit ? 'Edit category' : 'Add category';

  return (
    <AdminSubpage title={title} backTo="/categories">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Arabic Name</label>
              <input
                type="text"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <option value="both">Both</option>
                <option value="men">Men</option>
                <option value="women">Women</option>
              </select>
            </div>
            <div className="form-group">
              <label>Icon URL</label>
              <input
                type="url"
                value={formData.icon_url}
                onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                placeholder="https://example.com/icon.png"
              />
              {formData.icon_url ? (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={formData.icon_url}
                    alt="Preview"
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Update Category' : 'Create Category'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/categories')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default CategoryFormPage;
